import React, { useState,useMemo } from 'react';
import { TabView, TabPanel } from 'primereact/tabview';
import { Toast } from 'primereact/toast';
import { useSelector } from 'react-redux';
import { usePermissions } from '../../../hooks/usePermissions';
import { Roles } from '../../../utils/roleEnums';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useMaterialCatalog } from './hooks/useMaterialCatalog';
import {
    useLazyExportMaterialCatalogDetailToExcelQuery,
    useLazyExportMaterialCatalogMovementToExcelQuery,
    useLazyExportMaterialCatOtherCompanyToExcelQuery
} from '../../../api/endpoints/materialCatalog';
import MaterialCatalogFilters from './components/MaterialCatalogFilters';
import MaterialCatalogDetailTable from './components/MaterialCatalogDetailTable';
import MaterialCatalogMovementTable from './components/MaterialCatalogMovementTable';
import MaterialCatalogOtherTable from './components/MaterialCatalogOtherTable';
import GenericFileExportButton from '../../../components/common/GenericFileExportButton';

const MaterialCatalogPage = () => {
    const { th } = useLanguage();
    const [activeIndex, setActiveIndex] = useState(0);

    const userRoles = useSelector((state) => state.auth.roles ?? []);
    const { hasAnyRole } = usePermissions();
    const isDealer = hasAnyRole([Roles.DEALER, Roles.SERVICE_DEALER, Roles.SALES_DEALER]);

    const canSeeDealerDropdown = !isDealer && userRoles.some((r) => ['Admin', 'WarrantyTeam', 'WarrantyManager', 'WarrantyDirector'].includes(r));

    const {
        toastRef,
        detailItems,
        detailTotalCount,
        detailLoading,
        lazyParamsDetail,
        onPageDetail,
        movementItems,
        movementTotalCount,
        movementLoading,
        lazyParamsMovement,
        onPageMovement,
        otherItems,
        otherTotalCount,
        otherLoading,
        lazyParamsOther,
        onPageOther,
        handleApplyFilters,
        handleResetFilters,
        isDealer: hookIsDealer,
        detailExportParams,
        movementExportParams,
        otherExportParams
    } = useMaterialCatalog(activeIndex);

     const exportConfig = useMemo(() => {
        switch (activeIndex) {
            case 0:
                return {
                    useExportQuery: useLazyExportMaterialCatalogDetailToExcelQuery,
                    exportParams: detailExportParams,
                    hasData: !!detailItems?.length,
                    fallbackName: 'MalzemeKatalogu_Tanimlar.xlsx'
                };
            case 1:
                return {
                    useExportQuery: useLazyExportMaterialCatalogMovementToExcelQuery,
                    exportParams: movementExportParams,
                    hasData: !!movementItems?.length,
                    fallbackName: 'MalzemeKatalogu_Hareketler.xlsx'
                };
            case 2:
                return {
                    useExportQuery: useLazyExportMaterialCatOtherCompanyToExcelQuery,
                    exportParams: otherExportParams,
                    hasData: !!otherItems?.length,
                    fallbackName: 'MalzemeKatalogu_DigerFirmalar.xlsx'
                };
            default:
                return null;
        }
    }, [activeIndex, detailExportParams, movementExportParams, otherExportParams, detailItems, movementItems, otherItems]);

    return (
        <div className="card">
            <Toast ref={toastRef} />
       
            <div className="page-header mb-4 flex justify-content-between align-items-center">
                <h1 className="page-title mb-2">{th('materialCatalog.title')}</h1>
 
                {exportConfig && (
                    <GenericFileExportButton
                        key={activeIndex}
                        useExportQuery={exportConfig.useExportQuery}
                        exportParams={exportConfig.exportParams}
                        buttonLabel={th('common.export') || 'Excel'}
                        buttonClassName="p-button-success"
                        icon="pi pi-file-excel"
                        fileNameExtractor={(result) => result?.fileName || exportConfig.fallbackName}
                        dataExtractor={(result) => result?.fileBase64 || result?.blob}
                        contentTypeExtractor={(result) => result?.contentType}
                        exportFormat="auto"
                        disabled={!exportConfig.hasData}
                    />
                )}
            </div>

            <MaterialCatalogFilters activeIndex={activeIndex} canSeeDealerDropdown={canSeeDealerDropdown} isDealer={isDealer} onApplyFilters={handleApplyFilters} onResetFilters={handleResetFilters} />

            <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)} className="mt-4">
                <TabPanel
                    header={
                        <span>
                            <i className="pi pi-list mr-2" />
                            {th('materialCatalog.tabs.definitions')}
                        </span>
                    }
                >
                    {detailItems !== null && <MaterialCatalogDetailTable items={detailItems} lazyParams={lazyParamsDetail} totalRecords={detailTotalCount} loading={detailLoading} onPage={onPageDetail} th={th} isDealer={isDealer} />}
                </TabPanel>

                <TabPanel
                    header={
                        <span>
                            <i className="pi pi-history mr-2" />
                            {th('materialCatalog.tabs.movements')}
                        </span>
                    }
                >
                    {movementItems !== null && <MaterialCatalogMovementTable items={movementItems} lazyParams={lazyParamsMovement} totalRecords={movementTotalCount} loading={movementLoading} onPage={onPageMovement} th={th} isDealer={isDealer} />}
                </TabPanel>

                <TabPanel
                    header={
                        <span>
                            <i className="pi pi-building mr-2" />
                            {th('materialCatalog.tabs.otherCompanies')}
                        </span>
                    }
                >
                    {otherItems !== null && <MaterialCatalogOtherTable items={otherItems} lazyParams={lazyParamsOther} totalRecords={otherTotalCount} loading={otherLoading} onPage={onPageOther} th={th} />}
                </TabPanel>
            </TabView>
        </div>
    );
};

export default MaterialCatalogPage;
